import { useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  status: "loading" | "ready" | "error";
  onRetry: () => void;
};

const mondayIndex = (day: number) => (day + 6) % 7;

export default function EmptyMonthCalendar({ status, onRetry }: Props) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const locale = i18n.language === "fr" ? "fr-FR" : "en-GB";
  const firstWeekday = mondayIndex(month.getDay());
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const trailingDays = (7 - (firstWeekday + daysInMonth) % 7) % 7;
  const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(month);
  const today = new Date();

  const moveMonth = (offset: number) => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + offset, 1));
  };

  return (
    <section className="empty-calendar" aria-label={t("calendar.monthView")}>
      <div className="calendar-navigation">
        <button type="button" className="calendar-navigation__arrow" onClick={() => moveMonth(-1)} aria-label={t("calendar.previousMonth")}>
          <span aria-hidden="true">‹</span>
        </button>
        <button type="button" className="calendar-navigation__month" onClick={() => setMonth(new Date(today.getFullYear(), today.getMonth(), 1))} aria-label={t("calendar.currentMonth")}>
          {monthLabel}
        </button>
        <button type="button" className="calendar-navigation__arrow" onClick={() => moveMonth(1)} aria-label={t("calendar.nextMonth")}>
          <span aria-hidden="true">›</span>
        </button>
      </div>

      <div className={`calendar-message calendar-message--${status}`} role={status === "error" ? "alert" : "status"}>
        <span>{t(`calendar.${status === "ready" ? "noPractitioners" : status}`)}</span>
        {status === "error" && <button type="button" onClick={onRetry}>{t("calendar.retry")}</button>}
      </div>

      <div className="empty-calendar__grid" aria-label={monthLabel}>
        {Array.from({ length: 7 }, (_, index) => {
          const date = new Date(2024, 0, 1 + index);
          return <div key={`weekday-${index}`} className="empty-calendar__weekday">{new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date)}</div>;
        })}
        {Array.from({ length: firstWeekday }, (_, index) => <div key={`blank-${index}`} className="empty-calendar__day empty-calendar__day--outside" aria-hidden="true" />)}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1;
          const isToday = day === today.getDate() && month.getMonth() === today.getMonth() && month.getFullYear() === today.getFullYear();
          return (
            <div key={day} className={`empty-calendar__day${isToday ? " empty-calendar__day--today" : ""}`}>
              <time dateTime={`${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`}>{day}</time>
            </div>
          );
        })}
        {Array.from({ length: trailingDays }, (_, index) => <div key={`trailing-${index}`} className="empty-calendar__day empty-calendar__day--outside" aria-hidden="true" />)}
      </div>
    </section>
  );
}
